<template>
  <div class="flex flex-col h-full max-h-full overflow-hidden mobile-keyboard-safe">
    <!-- Chat Header -->
    <div class="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <!-- Mobile Back Button -->
          <button 
            @click="$emit('back')"
            class="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors bg-gray-50 border border-gray-200"
          >
            <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div class="relative">
            <div class="flex-shrink-0 h-10 w-10">
              <UserAvatar 
                :src="getValidAvatarUrl(contact?.avatar)" 
                :name="contact?.name || 'User'" 
                size="md"
              />
            </div>
            <div 
              class="absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white"
              :class="contact?.isOnline ? 'bg-green-500' : 'bg-gray-400'"
            ></div>
          </div>
          <div>
            <h2 class="text-lg font-semibold text-gray-900">{{ contact?.name || 'Conversation' }}</h2>
          </div>
        </div>
                 <div class="flex items-center gap-2">
           <span 
             class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
             :class="contact?.isOnline ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
           >
             <span 
               class="w-2 h-2 rounded-full mr-2"
               :class="contact?.isOnline ? 'bg-green-500' : 'bg-gray-400'"
             ></span>
             {{ contact?.isOnline ? 'Online' : 'Offline' }}
           </span>
         </div>
      </div>
    </div>

    <!-- Messages Area -->
    <div 
      ref="messagesContainer"
      class="flex-1 overflow-y-auto overflow-x-hidden p-6 space-y-4 min-h-0 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 messages-scroll-area"
    >
      <template v-for="(group, index) in groupedMessages" :key="index">
        <!-- Date Stamp -->
        <div class="flex justify-center my-4">
          <div class="bg-gray-100 text-gray-600 text-xs font-medium px-4 py-1.5 rounded-full shadow-sm">
            {{ group.date }}
          </div>
        </div>
        
        <!-- Messages for this date -->
        <div
          v-for="message in group.messages"
          :key="message.id"
          class="flex mb-3"
          :class="message.sender === userType ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-xs lg:max-w-md px-4 py-2 rounded-lg transition-opacity duration-200"
            :class="[
              message.sender === userType 
                ? message.isFailed 
                  ? 'bg-red-100 border-2 border-red-400 text-gray-900' 
                  : 'bg-blue-600 text-white'
                : 'bg-white text-gray-900 border border-gray-200',
              message.isPending ? 'opacity-70' : 'opacity-100'
            ]"
          >
            <p class="text-sm whitespace-pre-wrap break-words">{{ message.message }}</p>
            <div class="flex items-center justify-end mt-1 gap-1">
              <span 
                class="text-xs"
                :class="message.isFailed ? 'text-red-600' : (message.sender === userType ? 'text-blue-100' : 'text-gray-500')"
              >
                {{ message.isFailed ? 'Failed' : message.timestamp }}
              </span>
              <!-- Read receipts disabled - no status indicators shown -->
              <!-- Failed indicator -->
              <div v-if="message.isFailed" class="flex" title="Failed to send">
                <svg class="w-3.5 h-3.5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </template>
      
      <!-- Typing Indicator -->
      <div 
        v-if="isContactTyping" 
        class="flex justify-start mb-3 transition-all duration-200 ease-in-out"
      >
        <div class="bg-white text-gray-900 border border-gray-200 px-4 py-2 rounded-lg flex items-center gap-2">
          <span class="text-sm text-gray-600">{{ contact?.name || 'User' }} is typing</span>
          <div class="flex gap-1">
            <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0ms;"></span>
            <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 150ms;"></span>
            <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 300ms;"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Message Input -->
    <div class="bg-gradient-to-r from-white to-gray-50 border-t border-gray-200 p-6 flex-shrink-0 chat-input-container ios-keyboard-fix">
      <div class="flex gap-4">
        <div class="flex-1">
          <div class="relative">
            <textarea
              ref="messageInput"
              v-model="newMessage"
              @keydown="handleKeyDown"
              @focus="handleInputFocus"
              @blur="handleInputBlur"
              :placeholder="isCometChatLoading ? 'Initializing chat...' : 'Type your message...'"
              :disabled="isCometChatLoading"
              rows="1"
              maxlength="10000"
              class="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
              style="min-height: 52px; max-height: 120px;"
            ></textarea>
            <div class="absolute bottom-2 right-3 flex items-center space-x-2">
              <span 
                class="text-xs px-2 py-1 rounded-full font-medium"
                :class="{
                  'text-red-600 bg-red-100': newMessage.length >= 9500,
                  'text-orange-600 bg-orange-100': newMessage.length >= 9000 && newMessage.length < 9500,
                  'text-gray-400 bg-gray-100': newMessage.length < 9000
                }"
              >
                {{ newMessage.length }}/10,000
              </span>
            </div>
          </div>
          <p class="text-xs text-gray-500 mt-2 ml-1">
            Press <kbd class="px-1.5 py-0.5 bg-gray-100 rounded text-xs">Enter</kbd> to send, 
            <kbd class="px-1.5 py-0.5 bg-gray-100 rounded text-xs">Shift + Enter</kbd> for new line
          </p>
        </div>
        <button
          @click="sendMessage"
          :disabled="!newMessage.trim() || isCometChatLoading"
          class="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl hover:from-blue-700 hover:to-blue-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 self-start shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
        >
          <span class="font-medium">Send</span>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount, defineComponent, type PropType } from 'vue'
import { format, isToday, isYesterday, isSameDay } from 'date-fns'
import UserAvatar from '@/components/UserAvatar.vue'
import { scrollIntoViewSafe } from '@/utils/mobileViewport'
import { startTypingIndicator, endTypingIndicator } from '@/services/cometchat'
import { CometChat } from '@cometchat-pro/chat'

interface Message {
  id: string | number
  message: string
  timestamp: string
  sender: string
  isRead?: boolean
  sentAt?: number | string
  isPending?: boolean  // ⚡ Optimistic UI: Message is being sent
  isFailed?: boolean   // ⚠️ Optimistic UI: Message failed to send
}

interface Contact {
  id: string
  name: string
  avatar?: string
  isOnline?: boolean
}

export default defineComponent({
  name: 'ChatWindow',
  components: {
    UserAvatar
  },
  props: {
    contact: {
      type: Object as PropType<Contact>,
      required: false,
      default: null
    },
    messages: {
      type: Array as PropType<Message[]>,
      required: true
    },
    userType: {
      type: String,
      required: false,
      default: 'student',
      validator: (value: string) => ['student', 'tutor'].includes(value)
    },
    isCometChatLoading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['send-message', 'back'],
  setup(props, { emit }) {
    const newMessage = ref('')
    const messagesContainer = ref<HTMLElement | null>(null)
    const messageInput = ref<HTMLTextAreaElement | null>(null)
    const isContactTyping = ref(false)
    let typingTimeout: ReturnType<typeof setTimeout> | null = null
    const typingListenerId = `typing-listener-${Date.now()}`
    
    // Read receipts disabled - no status tracking
    
    // Setup typing indicator listeners only (no receipt listeners)
    onMounted(() => {
      if (props.contact) {
        const listener = new CometChat.MessageListener({
          onTypingStarted: (indicator: CometChat.TypingIndicator) => {
            const typingUser = indicator.getSender()
            if (typingUser.getUid() === props.contact?.id) {
              isContactTyping.value = true
              
              // Auto-hide after 5 seconds
              if (typingTimeout) clearTimeout(typingTimeout)
              typingTimeout = setTimeout(() => {
                isContactTyping.value = false
              }, 5000)
            }
          },
          onTypingEnded: (indicator: CometChat.TypingIndicator) => {
            const typingUser = indicator.getSender()
            if (typingUser.getUid() === props.contact?.id) {
              isContactTyping.value = false
              if (typingTimeout) clearTimeout(typingTimeout)
            }
          }
          // Read receipts disabled - onMessagesDelivered and onMessagesRead removed
        })
        
        CometChat.addMessageListener(typingListenerId, listener)
      }
    })
    
    // Cleanup typing listener
    onBeforeUnmount(() => {
      CometChat.removeMessageListener(typingListenerId)
      if (typingTimeout) clearTimeout(typingTimeout)
    })
    
    // Send typing indicator when user types
    let lastTypingTime = 0
    const sendTypingIndicator = async () => {
      const now = Date.now()
      // Only send typing indicator every 3 seconds to avoid spam
      if (now - lastTypingTime > 3000 && props.contact) {
        try {
          await startTypingIndicator(props.contact.id)
          lastTypingTime = now
          
          // Auto-stop typing after 3 seconds
          setTimeout(async () => {
            if (props.contact) {
              await endTypingIndicator(props.contact.id)
            }
          }, 3000)
        } catch (error) {
          // Silently fail - typing indicators are not critical
          console.debug('Failed to send typing indicator:', error)
        }
      }
    }
    
    // Watch for changes in input
    watch(newMessage, (newVal, oldVal) => {
      if (newVal && newVal !== oldVal) {
        sendTypingIndicator()
      }
    })
    
    // Helper function to validate avatar URLs
    const getValidAvatarUrl = (avatarUrl?: string | null) => {
      if (!avatarUrl) return null
      if (avatarUrl === '/images/default-avatar.png') return null
      if (avatarUrl === '') return null
      // Allow any non-empty string that's not the default avatar
      return avatarUrl
    }
    
    // Helper function to parse message date from timestamp or sentAt
    const getMessageDate = (message: Message) => {
      try {
        // Try to parse from sentAt first (ISO string from CometChat)
        if (message.sentAt) {
          // sentAt is usually a Unix timestamp in seconds
          const timestamp = typeof message.sentAt === 'number' 
            ? message.sentAt * 1000 // Convert to milliseconds
            : new Date(message.sentAt).getTime()
          return new Date(timestamp)
        }
        
        // Fallback to timestamp field (formatted string like "10:30 AM")
        // We'll use current date as base since timestamp only has time
        if (message.timestamp) {
          // This is a fallback - try to use any date stored in message
          return new Date()
        }
        
        return new Date()
      } catch (error) {
        console.warn('Error parsing message date:', error)
        return new Date()
      }
    }
    
    // Helper function to format date label
    const formatDateLabel = (date: Date) => {
      if (isToday(date)) {
        return 'Today'
      } else if (isYesterday(date)) {
        return 'Yesterday'
      } else {
        return format(date, 'MMMM d, yyyy')
      }
    }
    
    interface MessageGroup {
      date: string
      dateObj: Date
      messages: Message[]
    }
    
    // Group messages by date
    const groupedMessages = computed(() => {
      const groups: MessageGroup[] = []
      let currentGroup: MessageGroup | null = null
      
      props.messages.forEach(message => {
        const messageDate = getMessageDate(message)
        
        if (!currentGroup || !isSameDay(messageDate, currentGroup.dateObj)) {
          currentGroup = {
            date: formatDateLabel(messageDate),
            dateObj: messageDate,
            messages: []
          }
          groups.push(currentGroup)
        }
        
        currentGroup.messages.push(message)
      })
      
      return groups
    })
    
    
    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
        }
      })
    }
    
    const sendMessage = () => {
      const message = newMessage.value.trim()
      if (message) {
        // Validate message length
        if (message.length > 10000) {
          // This shouldn't happen due to maxlength, but add defensive check
          console.warn('Message exceeds 10,000 character limit')
          return
        }
        
        emit('send-message', message)
        newMessage.value = ''
        // Auto-resize textarea
        nextTick(() => {
          const textarea = document.querySelector('textarea')
          if (textarea) {
            textarea.style.height = 'auto'
            textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
          }
        })
        // Scroll to bottom after sending
        scrollToBottom()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        sendMessage()
      }
    }

    const handleInputFocus = () => {
      // Ensure input stays visible when virtual keyboard appears
      nextTick(() => {
        if (messageInput.value) {
          scrollIntoViewSafe(messageInput.value, { 
            behavior: 'smooth', 
            block: 'nearest' 
          })
        }
      })
    }

    const handleInputBlur = () => {
      // Optional: Handle when input loses focus
      // This can be used for additional cleanup if needed
    }

    // Watch for new messages and auto-scroll
    watch(() => props.messages, () => {
      scrollToBottom()
      // Read receipts disabled - no status tracking
    }, { deep: true })

    // Scroll to bottom on mount
    onMounted(() => {
      scrollToBottom()
      // Read receipts disabled - no status initialization
    })

    return {
      newMessage,
      messagesContainer,
      messageInput,
      isContactTyping,
      sendMessage,
      handleKeyDown,
      handleInputFocus,
      handleInputBlur,
      scrollToBottom,
      getValidAvatarUrl,
      groupedMessages
    }
  }
})
</script>
